import { Fragment, PropsWithChildren, ReactElement, useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { ProductFruits } from 'react-product-fruits';
import { useCurrentUserInProjectLabelled } from 'store/user';
import { ENVIRONMENTS } from 'shared/utils/environment/types';
import { getEnvironment } from 'shared/utils/environment/determineEnvironment';
import { isProductFruitsDisabled } from 'shared/utils/environment/variables';
import { debugLog } from 'shared/logger/debugLog';
import { logError } from 'setup/logger/logError';
import { ErrorBoundary } from '@sentry/react';

const PRODUCT_FRUITS_INDICATORS = [
  'productfruits',
  'product-fruits',
  'product_fruits',
  'loadforcurrentidentity',
  'load for current identity',
  'identifyuser',
  'identify user',
  'react-product-fruits',
  'productfruits.com',
  'product-fruits.com',
] as const;

const PRODUCT_FRUITS_FILENAME_INDICATORS = [
  'productfruits',
  'product-fruits',
  'react-product-fruits',
  'productfruits.com',
] as const;

const PRODUCT_FRUITS_STACK_INDICATORS = [
  'productfruits',
  'product-fruits',
  'react-product-fruits',
  'productfruits.com',
  'loadforcurrentidentity',
  'identifyuser',
] as const;

const PRODUCT_FRUITS_URL_INDICATORS = [
  'productfruits.com',
  'product-fruits.com',
] as const;

const PRODUCT_FRUITS_REJECTION_INDICATORS = [
  'productfruits',
  'product-fruits',
  'react-product-fruits',
  'load failed',
  'loadforcurrentidentity',
  'identifyuser',
] as const;

const PRODUCT_FRUITS_ERROR_MESSAGES = {
  ERROR_CAUGHT: 'Product Fruits error caught, disabling:',
  PROMISE_REJECTION_CAUGHT: 'Product Fruits promise rejection caught, disabling:',
  ERROR_BOUNDARY_CAUGHT: 'Product Fruits error caught by ErrorBoundary:',
  DISABLED_DUE_TO_ERROR: 'Product Fruits disabled due to error',
} as const;

function isProductFruitsError(error: Error | string | unknown): boolean {
  const errorString = typeof error === 'string' 
    ? error 
    : error instanceof Error 
      ? `${error.message} ${error.stack || ''} ${error.name || ''}`
      : String(error);

  const lowerErrorString = errorString.toLowerCase();
  
  return PRODUCT_FRUITS_INDICATORS.some(indicator => 
    lowerErrorString.includes(indicator)
  );
}

function isProductFruitsErrorEvent(event: ErrorEvent): boolean {
  if (event.message && isProductFruitsError(event.message)) {
    return true;
  }

  if (event.filename) {
    const filename = event.filename.toLowerCase();
    if (PRODUCT_FRUITS_FILENAME_INDICATORS.some(indicator => filename.includes(indicator))) {
      return true;
    }
  }

  if (event.error) {
    if (event.error.message && isProductFruitsError(event.error.message)) {
      return true;
    }
    if (event.error.stack && isProductFruitsError(event.error.stack)) {
      return true;
    }
  }

  if (event.error?.stack) {
    const stack = event.error.stack.toLowerCase();
    if (PRODUCT_FRUITS_STACK_INDICATORS.some(indicator => stack.includes(indicator))) {
      return true;
    }
  }

  const eventString = JSON.stringify(event).toLowerCase();
  if (PRODUCT_FRUITS_URL_INDICATORS.some(indicator => eventString.includes(indicator))) {
    return true;
  }

  return false;
}

function isProductFruitsRejection(reason: unknown): boolean {
  if (reason instanceof Error) {
    return isProductFruitsError(reason);
  }
  
  if (typeof reason === 'string') {
    return isProductFruitsError(reason);
  }

  if (reason && typeof reason === 'object') {
    const reasonString = JSON.stringify(reason).toLowerCase();
    return PRODUCT_FRUITS_REJECTION_INDICATORS.some(indicator => 
      reasonString.includes(indicator)
    );
  }

  return false;
}

export function WithProductFruits({
  children,
}: PropsWithChildren<{}>): ReactElement {
  const intl = useIntl();
  const user = useCurrentUserInProjectLabelled();
  const [hasError, setHasError] = useState(false);

  const userInfo = {
    username: user.label || 'unknown user',
    email: user.email || 'unknown email',
    role: user.role || 'unknown role',
  };
  const workspaceCode = getWorkspaceCodeByEnvironment(getEnvironment());

  const disableProductFruits = useCallback(() => {
    setHasError(true);
  }, []);

  useEffect(() => {
    // Product Fruits code still running after it is disabled keeps failing:
    // the first failure tells it was disabled, the next ones add nothing.
    let isFailureReported = false;
    const disableAfterFailure = (message: string, details: object): void => {
      if (!isFailureReported) {
        isFailureReported = true;
        logError(message, details);
      }
      setHasError(true);
    };

    const handleError = (event: ErrorEvent) => {
      if (isProductFruitsErrorEvent(event)) {
        disableAfterFailure(PRODUCT_FRUITS_ERROR_MESSAGES.ERROR_CAUGHT, {
          message: event.message,
          filename: event.filename,
          error: event.error,
          stack: event.error?.stack,
        });
      }
    };

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      if (isProductFruitsRejection(event.reason)) {
        disableAfterFailure(PRODUCT_FRUITS_ERROR_MESSAGES.PROMISE_REJECTION_CAUGHT, {
          reason: event.reason,
          type: typeof event.reason,
        });
      }
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);

  if (!workspaceCode || isProductFruitsDisabled() || hasError) {
    if (hasError) {
      debugLog(PRODUCT_FRUITS_ERROR_MESSAGES.DISABLED_DUE_TO_ERROR);
    }
    return <>{children}</>;
  }

  return (
    <>
      <ErrorBoundary
        fallback={<Fragment />}
        beforeCapture={(scope, error) => {
          if (error instanceof Error && isProductFruitsError(error)) {
            scope.setTag('productFruitsError', 'true');
            logError(PRODUCT_FRUITS_ERROR_MESSAGES.ERROR_BOUNDARY_CAUGHT, {
              error: error.message,
              stack: error.stack,
            });
            disableProductFruits();
          }
        }}
        showDialog={false}
      >
        <ProductFruits
          workspaceCode={workspaceCode}
          language={intl.locale.slice(0, 2)}
          user={userInfo}
          lifeCycle='unmount'
        />
      </ErrorBoundary>
      {children}
    </>
  );
}

function getWorkspaceCodeByEnvironment(environment: ENVIRONMENTS): string {
  switch (environment) {
    case ENVIRONMENTS.INTEGRATION_DEV:
    case ENVIRONMENTS.INTEGRATION:
      return 'cmyE7VLgNf1jfEix';
    case ENVIRONMENTS.STAGING_DEV:
    case ENVIRONMENTS.STAGING:
      return '75YhlIGxdBRuyO9w';
    case ENVIRONMENTS.PRODUCTION:
      return 'fKIRkkDT9zMXHLn6';
    case ENVIRONMENTS.DEVELOPMENT:
      return '';
  }
}
